import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { MessageCircle, X, Send, Sparkles, LifeBuoy } from "lucide-react";

const INTERNAL_PATHS = ["/booking", "/packages", "/contact", "/support", "/mavs-volleyball", "/locations", "/events", "/offers", "/faq", "/login"];

// Render assistant text, turning known internal paths into clickable links.
function renderText(text, onNavigate) {
  const pattern = new RegExp(`(${INTERNAL_PATHS.map((p) => p.replace("/", "\\/")).join("|")})`, "g");
  const parts = String(text).split(pattern);
  return parts.map((part, i) => {
    if (INTERNAL_PATHS.includes(part)) {
      // eslint-disable-next-line react/no-array-index-key
      return <Link key={i} to={part} onClick={onNavigate} className="font-semibold text-brand hover:underline">{part}</Link>;
    }
    // eslint-disable-next-line react/no-array-index-key
    return <span key={i}>{part}</span>;
  });
}

export const ChatWidget = () => {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/public/chat/config").then((r) => setConfig(r.data)).catch(() => setConfig({ enabled: false }));
  }, []);

  useEffect(() => {
    if (open && messages.length === 0 && config) {
      setMessages([{ role: "assistant", content: config.welcome }]);
    }
  }, [open, config, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  if (!config || !config.enabled) return null;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    const history = messages.filter((m) => m.content);
    const next = [...messages, { role: "user", content: msg }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/public/chat", { message: msg, history });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't respond right now. Please try our Support page (/support)." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Launcher button */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-launcher"
        aria-label={open ? "Close chat" : "Open chat"}
        className="fixed bottom-24 right-4 z-[200] flex h-14 w-14 items-center justify-center rounded-full bg-brand text-white shadow-lg shadow-brand/30 transition-transform duration-200 hover:scale-105 active:scale-95 sm:bottom-6 sm:right-6"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-40 right-4 z-[200] flex h-[28rem] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-border bg-card/95 shadow-2xl backdrop-blur-xl sm:bottom-24 sm:right-6"
        >
          {/* Header */}
          <div className="flex items-center gap-2.5 border-b border-border bg-black px-4 py-3 text-white">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand"><Sparkles className="h-4 w-4" /></span>
            <div className="min-w-0">
              <p className="truncate font-condensed text-sm font-semibold uppercase tracking-wide">{config.title}</p>
              <p className="text-[11px] text-white/60">Typically replies instantly</p>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
            {messages.map((m, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                <div className={cn(
                  "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
                  m.role === "user" ? "rounded-br-sm bg-brand text-white" : "rounded-bl-sm bg-muted text-foreground"
                )}>
                  {m.role === "assistant" ? renderText(m.content, () => setOpen(false)) : m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start"><div className="rounded-2xl rounded-bl-sm bg-muted px-3.5 py-2.5"><span className="flex gap-1"><span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.3s]" /><span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.15s]" /><span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" /></span></div></div>
            )}

            {/* Suggestions (only before user has asked anything) */}
            {messages.length <= 1 && config.suggestions?.length > 0 && (
              <div className="space-y-2 pt-1" data-testid="chat-suggestions">
                {config.suggestions.map((s, i) => (
                  <button key={s} onClick={() => send(s)} data-testid={`chat-suggestion-${i}`} className="block w-full rounded-lg border border-border px-3 py-2 text-left text-xs font-medium text-foreground transition-colors hover:border-[hsl(var(--brand-red))] hover:bg-accent">{s}</button>
                ))}
              </div>
            )}
          </div>

          {/* Guardrail note */}
          <Link to="/support" onClick={() => setOpen(false)} data-testid="chat-support-link" className="flex items-center justify-center gap-1.5 border-t border-border bg-muted/40 py-1.5 text-[11px] text-muted-foreground hover:text-brand transition-colors">
            <LifeBuoy className="h-3 w-3" /> Need account help? Submit a support ticket
          </Link>

          {/* Input */}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-border p-3">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question..."
              data-testid="chat-input"
              className="flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm outline-none focus-visible:border-[hsl(var(--brand-red))]"
            />
            <button type="submit" disabled={!input.trim() || sending} data-testid="chat-send" aria-label="Send" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand text-white transition-colors hover:bg-[hsl(var(--brand-red-deep))] disabled:opacity-50">
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
